import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { linkTokenFrom, verifyToken } from '~/lib/auth';

/**
 * /auth/callback#token=… — landing page for the sign-in email's link. Trades
 * the single-use token for a session JWT, then drops the operator on the
 * dashboard. New links carry the token in the URL fragment (it never reaches
 * a server log); a link emailed before 2026-10-21 (`?token=`) still works.
 * Either way the address bar is cleared before the token is redeemed. Errors
 * (expired/used link) get a friendly retry path.
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // Guard against React 18 StrictMode double-invoking the effect — the
  // token is single-use, so the second invocation would burn an "already
  // used" error after a successful first exchange.
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    const token = linkTokenFrom(window.location.hash, window.location.search);
    // The token has served its purpose once it leaves the address bar:
    // keep it out of history, referrers and anything that reads the URL.
    window.history.replaceState(null, '', '/auth/callback');
    if (!token) {
      setError('This link is incomplete. Open the whole link from your email, or ask for a new sign-in code.');
      return;
    }
    void verifyToken(token).then(({ error: err }) => {
      if (err) {
        setError(err);
        return;
      }
      navigate('/', { replace: true });
    });
  }, [navigate]);

  return (
    <div className="admin-login">
      <div className="admin-login__card">
        <h1 className="admin-login__title">Valley Vitality Admin</h1>
        {error ? (
          <>
            <div className="admin-login__error" role="alert">
              {error}
            </div>
            <p className="admin-login__sub" style={{ marginTop: 16 }}>
              <Link to="/login" style={{ color: 'var(--teal)', fontWeight: 600 }}>
                Get a new sign-in code →
              </Link>
            </p>
          </>
        ) : (
          <p className="admin-login__sub" role="status">
            Signing you in…
          </p>
        )}
      </div>
    </div>
  );
}
